import React, { forwardRef, useContext, useMemo } from 'react';
import { MaterialContext } from './component-types.js';
/**
 * A 9-slice panel component that renders a textured panel with customizable borders.
 * Uses 9-slice scaling technique to maintain border proportions while scaling the content area.
 *
 * @component
 * @example
 * ```tsx
 * <Panel9Slice
 *   texture={myTexture}
 *   borderSize={10}
 *   borderTextureSize={0.3}
 * >
 *   <Content />
 * </Panel9Slice>
 * ```
 *
 * @param {PanelProps & {texture?: Texture | null; borderSize?: number; borderTextureSize?: number}} props - The component props
 * @param {Texture | null} [props.texture] - Optional texture to apply to the panel. If provided, will override the default material texture
 * @param {number} [props.borderSize] - The size of the border in world units
 * @param {number} [props.borderTextureSize] - The size of the border in the texture, between 0.0 - 1.0 (default is 0.5, meaning half the texture size)
 * @param {Material | null} [props.material] - Optional custom material. If not provided, uses the cloned panel material from context
 * @param {React.ReactNode} props.children - Child elements to render inside the panel
 * @param {React.Ref<Object3D>} ref - Forward ref to access the underlying Object3D
 * @returns {React.ReactElement} A 9-slice panel element with the specified texture and border configuration
 */
export const Panel9Slice = forwardRef((props, ref) => {
    var _a;
    const context = useContext(MaterialContext);
    const mat = useMemo(() => { var _a; return (_a = context.panelMaterialTextured) === null || _a === void 0 ? void 0 : _a.clone(); }, []);
    if (mat && props.texture)
        mat.flatTexture = props.texture;
    return React.createElement('nineSlice', Object.assign(Object.assign({}, props), { material: (_a = props.material) !== null && _a !== void 0 ? _a : mat, ref: ref }), props.children);
});
Panel9Slice.displayName = 'Panel9Slice';
